'use client'

import { getIdToken } from 'firebase/auth'
import Cookies from 'js-cookie'
import Link from 'next/link'
import { useContext, useEffect, useRef, useState } from 'react'
import { useBottomScrollListener } from 'react-bottom-scroll-listener'
import { useTranslation } from 'react-i18next'
import { FaEyeSlash, FaLock, FaLongArrowAltRight } from 'react-icons/fa'
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
import { useRouter, useSearchParams } from 'next/navigation'
import { ClipLoader } from 'react-spinners'
import Modal from '../component/Modal'
import { staticProfiles } from '../config/staticImages'
import { useAuth } from '../contexts/AuthContext'
import { ChatImageContext } from '../contexts/ChatImageContext'
import {
  safeGetItem,
  safeRemoveItem,
  safeSetItem,
} from '../helpers/safeStorage'
import {
  activeButtonStyle,
  inactiveButtonStyle,
  isSafeCountry,
} from '../helpers/helpers'
import useFlag from '../hooks/useFlag'
import {
  characterFeedBody,
  characterFeedKey,
  withoutRepeats,
} from '../lib/character-feed'
import ChatCard from './ChatCard'
import DiscoverFilters from './DiscoverFilters'
import TutorialBanner from './TutorialBanner'
import DateGamesRow from './date/DateGamesRow'
import * as gemsApi from '../lib/api/gems.ts'

const conversation = [
  { text: 'Hey, how are you? \u{1F608}', type: 'sender' }, // 😈
  { text: "Heyyyy! I'm good, thanks! And you? \u{1F618}", type: 'recipient' }, // 😘
  { text: 'Doing well, send me a selfie \u{1F60D}\u{1F60D}', type: 'sender' }, // 😍😍
  { text: 'Haha alright, hows this? \u{1F352}\u{1F4A6}', type: 'recipient' }, // 🍒💦
  { text: 'photo', type: 'recipient' },
]

// `onMatch` is optional and additive. When omitted (every existing caller) the
// component behaves exactly as before: match, persist characterId, reload into
// the chat. The Telegram Mini App passes it instead, because there the handoff
// is "tell the bot who is active, then close the webview" — there is no page to
// reload into. Nothing else about the feed differs, which is the point: the
// webview shows the same Discover the site does, not a reimplementation of it.
const Discover = ({ onMatch }) => {
  const { t } = useTranslation()

  const searchParams = useSearchParams()
  const searchQuery = searchParams.get('searchQuery')
  const { user, safe, isPlusOrHigher } = useAuth()
  const router = useRouter()
  const safeCountry = isSafeCountry()

  const {
    activeButton,
    topImages,
    setTopImages,
    featuredImages,
    setFeaturedImages,
    model,
    setModel,
    images,
    setImages,
    filteredImages = [],
    setFilteredImages,
    gender,
    setGender,
    characterStyle,
    setCharacterStyle,
    sexuality,
    setSexuality,
    characterSort,
    setCharacterSort,
    characterFeeds,
    setCharacterFeeds,
  } = useContext(ChatImageContext)
  // Style (realistic / anime), orientation and a popular / newest sort, cached per combination (DiscoverFilters.tsx).
  // Off: the old style and gender selects, cached in topImages.
  const filtersOn = useFlag('character_feed_filters')
  const filters = {
    style: characterStyle,
    sexuality,
    gender,
    sort: characterSort,
  }
  const feedKey = filtersOn
    ? characterFeedKey(filters)
    : `legacy|${model}|${gender}`
  // The filters a response was asked for may no longer be the ones on screen (a change, or the flag turning on after
  // a page was asked for the old way): it is cached under its own key and shown only when they still are.
  const feedKeyRef = useRef(feedKey)
  feedKeyRef.current = feedKey
  // The filter combinations whose first page has been asked for: a change of filters while a page is loading is
  // fetched once that page is in, and a combination with no characters is not asked for again and again.
  const requestedFeeds = useRef(new Set())
  const [loadingImages, setLoadingImages] = useState(false)
  const [welcomeModal, setWelcomeModal] = useState(false)
  const [upgradeModal, setUpgradeModal] = useState(false)
  const [swipesExceededModal, setSwipesExceededModal] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [, setMessages] = useState([])
  const [hasStartedAnimation, setHasStartedAnimation] = useState(false)

  useEffect(() => {
    const chatContainer = document.querySelector('.chat-container')

    const observer = new IntersectionObserver(
      (entries) => {
        // Check if chat container is intersecting (visible) and animation hasn't started yet
        if (entries[0].isIntersecting && !hasStartedAnimation) {
          setHasStartedAnimation(true) // Prevent further animation triggers

          // Your existing logic to start sending messages
          if (user || conversation?.length === 0) {
            return
          }

          setMessages([conversation[0]]) // Initialize by setting the first message immediately.
          let i = 1 // Start from the second message.

          const sendNextMessage = () => {
            if (i < conversation?.length) {
              setMessages((msgs) => [...msgs, conversation[i]])
              i++
              // After each message, adjust the timer for the next one.
              const nextDelay = i === 1 ? 2000 : Math.random() * 2000 + 1000 // 2 seconds for the second, random 1-3 for the rest.
              setTimeout(sendNextMessage, nextDelay)
            }
          }

          // Schedule the second message. This uses a fixed delay as per the initial requirement.
          if (conversation?.length > 1) {
            setTimeout(sendNextMessage, 2000) // Start with a fixed delay for the second message.
          }
        }
      },
      {
        rootMargin: '0px',
        threshold: 0.1, // Trigger when 10% of the chat container is visible
      },
    )

    // Start observing the chat container
    if (chatContainer) {
      observer.observe(chatContainer)
    }

    // Cleanup observer on component unmount
    return () => {
      observer.disconnect()
    }
  }, [user, hasStartedAnimation, conversation]) // Ensure all relevant dependencies are listed

  useEffect(() => {
    const videoElement = document.querySelector('video')
    if (videoElement) {
      videoElement.play().catch((e) => {
        // AbortError = play() interrupted by a re-load (benign race); ignore it
        // but still surface genuine failures (e.g. NotAllowedError).
        if (e?.name !== 'AbortError') console.error(e)
      })
    }
  }, [])

  useEffect(() => {
    // safeGetItem: a bare localStorage read throws on blocked-storage iOS Safari and
    // would crash the discover view into the error boundary; the cookie fallback works.
    const welcomeBack =
      safeGetItem('welcomeBack100v8') || Cookies.get('welcomeBack100v8')
    if (welcomeBack === 'true') {
      setWelcomeModal(true)
    }
  }, [])

  const isPaidUser = isPlusOrHigher

  // The filtered feed's cached pages; a guest, who cannot change filters, sees the sample profiles.
  const showCharacterFeed = () => {
    const cached = characterFeeds[feedKey] || []
    setImages(cached.length > 0 || user ? cached : staticProfiles)
  }

  useEffect(() => {
    if (filtersOn) return showCharacterFeed()
    let imagesToSet = []

    setImages(imagesToSet)
    if (
      topImages[model][gender]?.length > 0 ||
      featuredImages[model][gender]?.length > 0
    ) {
      imagesToSet =
        activeButton === 'Top'
          ? topImages[model][gender]
          : featuredImages[model][gender]
      setImages(imagesToSet)
    } else if (!user) {
      setImages(staticProfiles)
    } else {
      loadMoreImages('Top')
    }
  }, [model, activeButton, gender, filtersOn, feedKey])

  useEffect(() => {
    if (!filtersOn || !user || fetching) return
    if (requestedFeeds.current.has(feedKey) || characterFeeds[feedKey]?.length)
      return
    loadMoreRef.current('Top')
  }, [filtersOn, user, fetching, feedKey, characterFeeds])

  useEffect(() => {
    setFilteredImages(images)
  }, [images])

  // A page arrived for the filters `requestKey`: cache it under them, and show it if they are still the ones on screen.
  const addPage = (requestKey, imageList) => {
    if (filtersOn) {
      setCharacterFeeds((prev) => ({
        ...prev,
        [requestKey]: withoutRepeats(prev[requestKey], imageList),
      }))
    } else if (activeButton === 'Top') {
      setTopImages((prevState) => ({
        ...prevState,
        [model]: {
          ...prevState[model],
          [gender]: [...(prevState[model][gender] || []), ...imageList],
        },
      }))
    } else {
      setFeaturedImages((prevState) => ({
        ...prevState,
        [model]: {
          ...prevState[model],
          [gender]: [...(prevState[model][gender] || []), ...imageList],
        },
      }))
    }
    if (feedKeyRef.current === requestKey) {
      setImages((prevImages) => withoutRepeats(prevImages, imageList))
    }
  }

  // What /api/get_chat_feed is asked for the filters on screen: the page after the last character already cached.
  const feedRequest = () => {
    const lastId = (list) => list?.[list.length - 1]?.id || null
    if (!filtersOn) {
      return {
        last_doc_id_newest: lastId(featuredImages[model][gender]),
        last_doc_id: lastId(topImages[model][gender]),
        safe,
        model,
        gender,
      }
    }
    requestedFeeds.current.add(feedKey)
    return characterFeedBody(lastId(characterFeeds[feedKey]), safe, filters)
  }

  const loadMoreImages = async (feed) => {
    if (!fetching) {
      const fetchImages = async () => {
        try {
          if (loadingImages || searchQuery) {
            return
          }

          setLoadingImages(true)
          setFetching(true)

          const idToken = await user.getIdToken()

          const endpoint = `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/get_chat_feed`

          const requestKey = feedKey
          const response = await fetch(endpoint, {
            method: 'POST',
            body: JSON.stringify(feedRequest()),
            headers: {
              'Content-Type': 'application/json',
              Authorization: idToken,
            },
          })

          if (response.ok) {
            const responseData = await response.json()
            const newImages = responseData.images

            if (newImages?.length > 0) {
              const imageList = []

              newImages.forEach((data) => {
                imageList.push({
                  id: data.id,
                  imageUrl: data.imageUrl,
                  age: data.age,
                  bio: data.bio,
                  scenario: data.scenario,
                  bodyType: data.bodyType,
                  breastSize: data.breastSize,
                  dateUploaded: data.date_uploaded,
                  ethnicity: data.ethnicity,
                  fashion: data.fashion,
                  gender: data.gender,
                  location: data.location,
                  model: data.model,
                  occupation: data.occupation,
                  personality: data.personality,
                  portraitImage: data.portraitImage,
                  prompt: data.prompt,
                  saves: data.saves,
                  uid: data.uid,
                  views: data.views,
                  name: data.name,
                  sexuality: data.sexuality,
                  video: data.video,
                  // Pointer to a published date game ({version, title, cover_url}), merged
                  // onto profiles/{id} by the publisher; ChatCard shows its Date button on it.
                  game: data.game,
                })
              })

              addPage(requestKey, imageList)
            }
          }
        } catch {
          // console.log(error)
        } finally {
          setLoadingImages(false)
          setFetching(false)
        }
      }

      return await fetchImages()
    }
  }

  const loadMoreRef = useRef(loadMoreImages)
  loadMoreRef.current = loadMoreImages

  useBottomScrollListener(() => loadMoreImages('Top'), {
    offset: 2000, // Offset from the bottom of the page. Adjust this value as per your requirement.
    debounce: 1000,
  })

  const claimWelcomeGems = async () => {
    try {
      const idToken = await user.getIdToken()

      const response = await gemsApi.claimWelcomeReward(idToken)

      // The offer is spent once it is paid (2xx) or found claimed already (403); after any other answer the flag
      // stays, so the offer comes back on the next visit.
      if (response.ok || response.status === 403) {
        safeRemoveItem('welcomeBack100v8')
        Cookies.remove('welcomeBack100v8')
      }

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Something went wrong')
      }

      if (typeof window !== 'undefined') window.location.reload()
    } catch (err) {
      console.log(err)
    }
    setWelcomeModal(false)
  }

  const handleMatch = async (image) => {
    if (!user) {
      // safeSetItem: a bare setItem throws here on blocked-storage iOS Safari,
      // before the try below — the guest's Match tap would silently do nothing.
      safeSetItem('offlineCharacter', JSON.stringify(image))
      router.push('/chat')
      // Without this return, execution fell into getIdToken(null) → catch → finally,
      // whose window.location.reload() clobbered the pending /chat navigation — guests
      // got a reloaded Discover instead of the chat handoff.
      return
    }

    try {
      const idToken = await getIdToken(user)
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/match_profile`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            id: image.id,
          }),
        },
      )

      await response.json()

      if (response.ok) {
        safeSetItem('characterId', image.id)
        if (onMatch) {
          await onMatch(image)
          return
        }
        if (typeof window !== 'undefined') window.location.reload()
      } else {
        throw new Error('Response not ok')
      }
    } catch (error) {
      console.error('Something went wrong.', error)
    } finally {
      // safeSetItem: a bare setItem here throws on blocked-storage iOS Safari inside
      // `finally`, skipping the reload and escaping as an unhandled rejection.
      safeSetItem('characterId', image.id)
      // The reload is skipped when a caller owns the handoff (the Mini App closes
      // the webview instead). `finally` runs even after the `return` above, so
      // without this guard the webview would reload itself out from under the
      // close.
      if (!onMatch && typeof window !== 'undefined') window.location.reload()
    }
  }

  return (
    <div className="bg-[var(--background-bg-color)] min-h-screen">
      <Modal
        isOpen={swipesExceededModal}
        closeModal={() => setSwipesExceededModal(false)}
        title="Likes Exceeded"
        content="You are out of Likes. Upgrade for Unlimited Likes, or comeback tomorrow."
        confirmation="Upgrade Now"
        onConfirm={() => router.push('/upgrade')}
      />

      <Modal
        isOpen={welcomeModal}
        closeModal={() => setWelcomeModal(false)}
        title="Welcome Back!"
        content="We have made a lot of improvements. Here's 50 Gems to test them out."
        confirmation="Claim Gems"
        onConfirm={user ? claimWelcomeGems : () => router.push('/signup')}
      />

      <Modal
        isOpen={upgradeModal}
        closeModal={() => setUpgradeModal(false)}
        title={
          <div className="flex items-center">
            <FaLock className="text-black opacity-70 z-20 mr-2" />
            <h1>NSFW Content</h1>
          </div>
        }
        content="You must sign up to view NSFW content."
        confirmation="Sign Up"
        onConfirm={() => router.push('/signup')}
      />

      <div className="mx-auto">
        <div className="py-3 px-4 sm:px-6 lg:px-8 mx-auto">
          <section className="container mx-auto md:px-4 py-8">
            <>
              {/* Stories row (2026-10-05): the same row as Explore's, first thing in the
                  browse view — a story is a way into a character, which is what Discover is
                  for. Above the "Discover Profiles" header rather than under it, so the style
                  and gender filters stay next to the grid they filter. Explore's rule for
                  `enabled`: never in safe mode or a safe country (NSFW; the backend returns []
                  there too), so guests, who default to safe mode, never see it. Also off on a
                  search (the page is then a result list) and in the Telegram picker (`onMatch`:
                  its one job is choosing a companion for the bot, and a story would navigate
                  out of the webview's flow). The row's module-level feed cache means Explore
                  and Discover share one fetch per page load. */}
              {/* On a phone the chat page's own padding left the cards ~220px wide: the row
                  bleeds into it below sm. */}
              <div className="-mx-4 sm:mx-0">
                <DateGamesRow
                  enabled={
                    safe === false && !safeCountry && !searchQuery && !onMatch
                  }
                />
              </div>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-4 ">
                <p className="text-white font-semibold mt-4 text-xl">
                  Discover Profiles
                </p>

                {filtersOn ? (
                  <DiscoverFilters
                    style={characterStyle}
                    sexuality={sexuality}
                    gender={gender}
                    sort={characterSort}
                    onChange={(filter, value) => {
                      if (!user) {
                        router.push('/signup')
                        return
                      }
                      const set = {
                        style: setCharacterStyle,
                        sexuality: setSexuality,
                        gender: setGender,
                        sort: setCharacterSort,
                      }[filter]
                      set(value)
                    }}
                  />
                ) : (
                  <div className="flex w-full lg:w-auto gap-2 lg:gap-0 mt-2 lg:mt-0 items-center lg:flex-wrap">
                    <div className="relative inline-flex flex-1 lg:flex-none lg:pl-2 my-1">
                      <svg
                        className="w-2 h-2 absolute top-0 right-0 m-4 pointer-events-none"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 412 232"
                      >
                        <path
                          d="M206 232l206-232H0z"
                          fill="#FFF"
                          fillRule="evenodd"
                        ></path>
                      </svg>
                      <select
                        value={model}
                        onChange={(e) => {
                          user
                            ? setModel(e.target.value)
                            : router.push('/signup')
                        }}
                        className={`rounded-full font-semibold w-full lg:w-40 text-center h-9 px-6 lg:px-8 hover:border-gray-400 focus:outline-none appearance-none text-sm cursor-pointer ${
                          model === 'all'
                            ? inactiveButtonStyle
                            : activeButtonStyle
                        }`}
                      >
                        <option value="all">{t('allStyles')}</option>
                        <option value="Real">{t('real')}</option>
                        <option value="Hyperreal"> {t('hyperreal')}</option>
                        <option value="Anime">{t('anime')}</option>
                        <option value="Hyperanime">{t('hyperanime')}</option>
                        <option value="K-Pop">{t('k-pop')}</option>
                      </select>
                    </div>

                    <div className="relative inline-flex flex-1 lg:flex-none lg:pl-2 my-1">
                      <svg
                        className="w-2 h-2 absolute top-0 right-0 m-4 pointer-events-none"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 412 232"
                      >
                        <path
                          d="M206 232l206-232H0z"
                          fill="#FFF"
                          fillRule="evenodd"
                        ></path>
                      </svg>

                      <select
                        value={gender}
                        onChange={(e) => {
                          user
                            ? setGender(e.target.value)
                            : router.push('/signup')
                        }}
                        className={`rounded-full font-semibold w-full lg:w-40 text-center h-9 px-6 lg:px-8 hover:border-gray-400 focus:outline-none appearance-none text-sm cursor-pointer ${
                          gender === 'all'
                            ? inactiveButtonStyle
                            : activeButtonStyle
                        }`}
                      >
                        <option value="all">All Genders</option>
                        <option value="woman">Women</option>
                        <option value="man">Men</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between my-2">
                {safe && !safeCountry && (
                  <Link
                    href="/settings"
                    className="mt-2 inline-flex max-w-lg flex-wrap items-center justify-center gap-2 rounded-2xl border border-[#7a74eb]/25 px-3 py-2 text-sm transition md:ml-auto md:mx-0 mx-auto"
                  >
                    <FaEyeSlash className="text-[#a5b4fc] shrink-0" />

                    <span className="text-gray-300">
                      <span className="font-medium text-[#c7d2fe]">
                        {t('safeModeEnabled')}
                      </span>
                    </span>

                    <span className="inline-flex items-center rounded-full border border-[#7a74eb]/50 px-3 py-1 text-xs font-semibold text-[#c7d2fe] transition hover:bg-[var(--main-bg-color)]">
                      {t('update')}
                    </span>
                  </Link>
                )}
              </div>
              <TutorialBanner
                storageKey="chatTutorialSeen"
                href="/articles/promptchan-chat-tutorial"
                label={t('howToChat')}
              />
              {filteredImages && filteredImages?.length > 0 ? (
                <>
                  {isPaidUser ? (
                    <ResponsiveMasonry
                      columnsCountBreakPoints={{
                        0: 1,
                        1024: 2,
                        1660: 3,
                        1920: 4,
                      }}
                    >
                      <Masonry gutter="0.5rem">
                        {Array.from({ length: filteredImages?.length }).map(
                          (_, index) => {
                            const image = filteredImages[index]
                            if (!image) return null

                            // Array to store the Card and potentially an AdCard
                            const elements = []

                            // Push the main card
                            elements.push(
                              <ChatCard
                                {...image}
                                // Play follows the Stories row's rule: never in safe mode, a safe
                                // country, or the Telegram picker (onMatch), where a story would
                                // navigate out of the companion-picking flow (2026-10-05 review).
                                game={
                                  safe === false && !safeCountry && !onMatch
                                    ? image.game
                                    : null
                                }
                                explore={true}
                                onMatch={() => handleMatch(image)}
                                suppressUpgrade={!!onMatch}
                                key={`browse${image.id}${index}`}
                                index={index}
                                safe={safe}
                              />,
                            )

                            // Check if the index is a multiple of 12 and not 0, then add AdCard
                            // if ((index + 1) % 20 === 0 && index !== 0) {
                            //   elements.push(
                            //     <div className="ring-1 ring-[var(--secondary-bg-color)] overflow-hidden rounded-md">
                            //       <AdCard key={`ad-${index}`} />
                            //     </div>
                            //   )
                            // }

                            return elements
                          },
                        )}
                      </Masonry>
                    </ResponsiveMasonry>
                  ) : (
                    <ResponsiveMasonry
                      columnsCountBreakPoints={{
                        350: 1,
                        750: 1,
                        1000: 2,
                        1750: 3,
                      }}
                    >
                      <Masonry gutter="0.5rem">
                        {Array.from({ length: filteredImages?.length }).map(
                          (_, index) => {
                            const image = filteredImages[index]
                            if (!image) return null

                            return (
                              <ChatCard
                                {...image}
                                // Play follows the Stories row's rule: never in safe mode, a safe
                                // country, or the Telegram picker (onMatch), where a story would
                                // navigate out of the companion-picking flow (2026-10-05 review).
                                game={
                                  safe === false && !safeCountry && !onMatch
                                    ? image.game
                                    : null
                                }
                                explore={true}
                                onMatch={() => handleMatch(image)}
                                suppressUpgrade={!!onMatch}
                                key={`browse${image.id}${index}`}
                                index={index}
                              />
                            )
                          },
                        )}
                      </Masonry>
                    </ResponsiveMasonry>
                  )}

                  <>
                    {/* Safe-country users can't turn Safe Mode off (the settings
                        toggle is hidden for them), so never send them there. */}
                    {safe && !safeCountry && (
                      <Link href="/settings">
                        <div className="flex items-center justify-center px-5 w-72 max-w-full py-3 my-20 bg-[var(--main-bg-color)] text-white rounded-full focus:outline-none ml-auto mr-auto font-semibold shadow-lg  text-center transition-transform duration-200 hover:scale-[1.05] text-sm">
                          Disable Safe Mode to View More
                          <FaLongArrowAltRight className="ml-2" />
                        </div>
                      </Link>
                    )}
                  </>
                </>
              ) : loadingImages ? (
                <p className="text-black text-center my-12">
                  <ClipLoader color="#ffffff" size={24} />
                </p>
              ) : (
                <p className="text-black text-center my-12">No results found</p>
              )}
            </>
          </section>
        </div>
      </div>
    </div>
  )
}

export default Discover
